import * as yup from "yup";

let supportEmail = "";
export const minDeliveryDate = getDateAfterWeekdays(10);

function getEndPointURL() {
    const hostname = document.location.hostname;
    const isDebug = ["localhost", "192.168."].some((part) => hostname.includes(part));

    let apiEnv;

    if (isDebug) {
        apiEnv = "debug";
    } else if (hostname.includes("test.")) {
        apiEnv = "test";
    } else if (hostname.includes("preprod.")) {
        apiEnv = "preprod";
    } else {
        apiEnv = "live";
    }

    const URLs = {
        live: "https://lysaghtlivingdesigner.lysaght.com/api/",
        preprod: "https://preprod.lysaghtlivingdesigner.lysaght.com/api/",
        test: "https://test.lysaghtlivingdesigner.lysaght.com/api/",
        debug: "http://localhost:62027/api/",
    };

    return URLs[apiEnv] || URLs["live"];
}

export async function fetchOffering(state, abortSignal) {
    if (!state) throw new Error("State is required");

    const API_URL = getEndPointURL();
    const endPointPath = `ExternalJob/GetSchema?Business=${encodeURIComponent(state)}&DesignType=${encodeURIComponent(
        "National Carport"
    )}`;

    const url = API_URL + endPointPath;

    try {
        const response = await fetch(url, {
            method: "GET",
            signal: abortSignal,
            headers: {
                Accept: "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Server responded with ${response.status}`);
        }

        const data = await response.json();
        supportEmail = data?.supportEmail || "";

        return data;
    } catch (err) {
        console.error("Error fetching offering:", err);
        throw err;
    }
}

export async function sendFormInput(input) {
    const API_URL = getEndPointURL();
    const endPointPath = "ExternalJob/Import";
    const url = API_URL + endPointPath;

    const getClientInfo = () => {
        const userAgent = navigator.userAgent;
        const platform = navigator.platform;

        let os = "Unknown";
        if (/Win/i.test(platform)) os = "Windows";
        else if (/Mac/i.test(platform)) os = "macOS";
        else if (/Linux/i.test(platform)) os = "Linux";
        else if (/Android/i.test(userAgent)) os = "Android";
        else if (/iPhone|iPad|iPod/i.test(userAgent)) os = "iOS";

        let browser = "Unknown";
        if (/Edg/i.test(userAgent)) browser = "Microsoft Edge";
        else if (/OPR/i.test(userAgent)) browser = "Opera";
        else if (/Chrome/i.test(userAgent)) browser = "Chrome";
        else if (/Safari/i.test(userAgent)) browser = "Safari";
        else if (/Firefox/i.test(userAgent)) browser = "Firefox";
        else if (/MSIE|Trident/i.test(userAgent)) browser = "Internet Explorer";

        const isMobile = /Mobi|Android/i.test(userAgent);
        const device = isMobile ? "Mobile" : "Desktop";

        return { device, os, browser, userAgent, platform };
    };

    let ip = "Unknown",
        city = "Unknown",
        country_name = "Unknown";
    try {
        const response = await fetch("https://ipapi.co/json/");
        const json = await response.json();
        ip = json.ip ?? "Unknown";
        city = json.city ?? "Unknown";
        country_name = json.country_name ?? "Unknown";
    } catch (err) {
        console.warn("Could not fetch IP info:", err);
    }

    const brandCapitalized =
        typeof brand === "string" && brand.length ? brand.charAt(0).toUpperCase() + brand.slice(1) : "";

    const clientInfo = getClientInfo();
    const payload = {
        ...input,
        sender: {
            ip,
            city,
            country: country_name,
            ...clientInfo,
        },
        supportEmail: supportEmail,
        brand: brandCapitalized,
    };

    try {
        const resp = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json", apiKey: "aHef98s0e7f09HHHfosoOOSk32993HSm" },
            body: JSON.stringify(payload),
        });

        if (!resp.ok) {
            const errorBody = await resp.json();

            throw new Error(
                errorBody.DeveloperMessage && errorBody.UserMessage
                    ? `${errorBody.UserMessage}: ${errorBody.DeveloperMessage}`
                    : "An unexpected error occurred. Please contact Lysaght for assistance."
            );
        }
    } catch (error) {
        throw error;
    }
}

export const schema = yup.object().shape({
    isBusinessUser: yup.boolean(),
    customer: yup.object().shape({
        firstName: yup
            .string()
            .required("First name is required")
            .matches(/^[A-Za-z\s]+$/, "First name can only contain letters and spaces"),
        lastName: yup
            .string()
            .required("Last name is required")
            .matches(/^[A-Za-z\s]+$/, "Last name can only contain letters and spaces"),
        phone: yup
            .string()
            .required("Phone is required")
            .min(8, "Enter a valid phone number")
            .max(16)
            .matches(/^[\d\s]+$/, "Phone number can only contain digits and spaces"),
        email: yup.string().email("Enter a valid email").required("Email is required"),
    }),
    site: yup.object().shape({
        streetAddress: yup.string().required("Site address is required"),
        state: yup.string().required("Site state is required"),
        postcode: yup.string(),
        isCyclonic: yup.string().required("Select Yes or No"),
    }),
    business: yup.object().when("isBusinessUser", {
        is: true,
        then: yup.object({
            name: yup
                .string()
                .required("Business name is required")
                .matches(/^[A-Za-z\s]+$/, "Business name can only contain letters and spaces"),
            contactName: yup
                .string()
                .required("Contact name is required")
                .matches(/^[A-Za-z\s]+$/, "Contact name can only contain letters and spaces"),
            phone: yup
                .string()
                .required("Business phone is required")
                .min(8, "Enter a valid phone number")
                .max(16)
                .matches(/^[\d\s]+$/, "Phone number can only contain digits and spaces"),
            email: yup.string().email("Enter a valid email").required("Business email is required"),
            accountCode: yup
                .string()
                .max(20)
                .transform((value) => (value === "" ? null : value))
                .nullable()
                .notRequired()
                .matches(/^[A-Za-z0-9\s]+$/, "Account code can only contain letters and digits"),
            purchaseOrderNo: yup
                .string()
                .max(20)
                .transform((value) => (value === "" ? null : value))
                .nullable()
                .notRequired()
                .matches(/^[A-Za-z0-9\s]+$/, "Order number can only contain letters and digits"),
        }),
        otherwise: yup.object().shape({}),
    }),
    design: yup.object().shape({
        carportType: yup.string().required("Carport type is required"),
        roofSheet: yup.string().required("Roof Sheeting is required"),
        roofSheetColour: yup.string().when("roofSheet", (roofSheet, schema) => {
            const isZincalume = typeof roofSheet === "string" && roofSheet.toLowerCase().includes("zincalume");
            return isZincalume ? schema.notRequired() : schema.required("Roof colour is required");
        }),
        gutter: yup.string().required("Gutter is required"),
        gutterColour: yup.string().required("Gutter colour is required"),
        frameColour: yup.string().required("Beams & posts colour is required"),
        flashingColour: yup.string().required("Flashing colour is required"),
        includeDownpipe: yup.boolean(),
        boltToSlabFootingCount: yup.number(),
    }),
    delivery: yup.object().shape({
        location: yup.string(),
        preferredDate: yup
            .date()
            .typeError("Preferred delivery date must be a valid date")
            .required("Preferred delivery date is required")
            .min(minDeliveryDate, `Preferred delivery date must be at least 10 business days from today`),
    }),
    notes: yup.object().shape({
        text: yup.string(),
    }),
});

const brands = ["tthg"];

export const defaultValues = {
    isBusinessUser: true,
    customer: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
    },
    site: {
        streetAddress: "",
        state: "",
        isCyclonic: "No",
    },
    business: {
        name: "",
        contactName: "",
        phone: "",
        email: "",
        accountCode: "",
        purchaseOrderNo: "",
    },
    design: {
        carportType: "",
        roofSheet: "",
        roofSheetColour: "",
        gutter: "",
        gutterColour: "",
        frameColour: "",
        includeDownpipe: true,
        boltToSlabFootingCount: 0,
    },
    delivery: {
        location: "Site",
        preferredDate: minDeliveryDate,
    },
    notes: {
        text: "",
    },
};

export const states = ["QLD", "NSW", "NT", "SA", "VIC", "WA", "TAS"];
export const cyclonicStates = ["QLD", "NT", "WA"];

export const deliveryOptions = [
    { value: "Site", label: "Site" },
    { value: "Store", label: "Store" },
    { value: "Pickup", label: "Pickup" },
];

export const boltToSlabOptions = [
    { value: 0, label: "None (All 4 in ground)" },
    { value: 1, label: "One (3 in ground)" },
    { value: 2, label: "Two (2 in ground)" },
    { value: 3, label: "Three (1 in ground)" },
    { value: 4, label: "Four (All bolt to slab)" },
];

export const canadaGibsonFont = {
    fontFamily: "canada-type-gibson",
    "& .MuiInputBase-root": {
        fontFamily: "canada-type-gibson",
    },
    "& .MuiInputLabel-root": {
        fontFamily: "canada-type-gibson",
    },
    "& .MuiFormHelperText-root": {
        fontFamily: "canada-type-gibson",
    },
    "& .MuiMenuItem-root": {
        fontFamily: "canada-type-gibson",
    },
};

const colourMap = {
    Basalt: { copyright: "Basalt®", hex: "#656668" },
    Bluegum: { copyright: "Bluegum®", hex: "#969799" },
    "Classic Cream": { copyright: "Classic Cream™", hex: "#e7d4b0" },
    "Cottage Green": { copyright: "Cottage Green®", hex: "#0e4a39" },
    "Deep Ocean": { copyright: "Deep Ocean®", hex: "#273c4f" },
    "Dover White": { copyright: "Dover White™", hex: "#f9fbf1" },
    Dune: { copyright: "Dune®", hex: "#a8a59c" },
    "Evening Haze": { copyright: "Evening Haze®", hex: "#bfb9a1" },
    Gully: { copyright: "Gully®", hex: "#7f776e" },
    Ironstone: { copyright: "Ironstone®", hex: "#343e4c" },
    Jasper: { copyright: "Jasper®", hex: "#675d51" },
    "Manor Red": { copyright: "Manor Red®", hex: "#65100f" },
    Monument: { copyright: "Monument®", hex: "#2c2d31" },
    "Night Sky": { copyright: "Night Sky®", hex: "#000000" },
    "Pale Eucalypt": { copyright: "Pale Eucalypt®", hex: "#74826b" },
    Paperbark: { copyright: "Paperbark®", hex: "#c6b69b" },
    "Shale Grey": { copyright: "Shale Grey™", hex: "#b1b6b1" },
    Southerly: { copyright: "Southerly®", hex: "#d2d1cb" },
    Surfmist: { copyright: "Surfmist®", hex: "#dcdacc" },
    "Thredbo White": { copyright: "Thredbo White®", hex: "#F4F4F4" },
    Wallaby: { copyright: "Wallaby®", hex: "#767572" },
    Windspray: { copyright: "Windspray®", hex: "#888b8a" },
    "Woodland Grey": { copyright: "Woodland Grey®", hex: "#414440" },
    Mangrove: { copyright: "Mangrove®", hex: "#737562" },
    Grey: { copyright: "Grey", hex: "#bdbeb9" },
};

export const brand = getBrand();

export function addTradeMark(colourString) {
    if (!colourString || typeof colourString !== "string") return colourString;

    const [primaryColour, secondaryColour] = colourString.split("/").map((s) => s.trim());

    const primary = colourMap[primaryColour];
    const secondary = colourMap[secondaryColour];

    if (!primary) {
        return colourString;
    }

    const formattedName = `${primary.copyright}${secondary ? `/${secondary.copyright}` : ""}`;

    return {
        [formattedName]: `${primary.hex},${secondary ? secondary.hex : primary.hex}`,
    };
}

export function getDateAfterWeekdays(n) {
    const date = new Date();
    let added = 0;

    while (added < n) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) {
            added++;
        }
    }

    return date.toISOString().split("T")[0];
}

export function formatDate(date) {
    if (!(date instanceof Date)) date = new Date(date);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

export function getBrand() {
    const params = new URLSearchParams(window.location.search);
    const brandParam = params.get("brand");
    const brand = brandParam?.trim().toLowerCase();

    return brands.includes(brand) ? brand : "lysaght";
}

export function useBrandTheme(brand) {
    return brandThemes[brand];
}

const brandThemes = {
    tthg: {
        name: "default",
        logo: null,
        slogan: "",
        headerTitle: "NATIONAL CARPORT",
        headerSubtitle: "ORDER FORM",
        primary: "#00234B",
        headerGradient: "linear-gradient(to bottom, #0a7030, #7ccf87)",
        headerShape: "none",
        shapeBg: "#ffffff",
    },
    lysaght: {
        name: "lysaght",
        logo: "/app/images/lysaght.svg",
        slogan: "INSPIRATION TO BUILD BETTER",
        headerTitle: "NATIONAL CARPORT",
        headerSubtitle: "ORDER FORM",
        primary: "#00234B",
        headerGradient: "linear-gradient(to bottom, #026BB5, #52C9F4)",
        headerShape: "block",
        shapeBg: "#ffffff",
    },
};
